<script setup>
import { stripBlockTags } from '@/composables/helpers/html';
import { storeToRefs } from 'pinia';
import { useGlobalStore } from '@/stores/global'

const props = defineProps({
    headline: { type: String, required: true },
    subHead: { type: String, required: false },
    excludeDecoration: { type: Boolean },
    embedScript: { type: String, required: true },
    forceWhite: { type: Boolean, required: false }
});

const loading = ref(true);
const { pageModel } = storeToRefs(useGlobalStore());
const isBlog = pageModel.value === 'blog';
const loader = ref(null)

const generateRandomUDID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

const formID = computed(() => {
    return 'form-container-' + generateRandomUDID();
})

const parseHubSpotData = (unescapedData) => {
    const formProps = {
        target: `#${formID.value}`
    };
    // Try to parse using named groups first
    const regexReturn = unescapedData.match(
        /(?<=region: ")(?<region>.+?)"[\s\S]+?portalId: "(?<portalId>.+?)"[\s\S]+?formId: "(?<formId>.+)"/
    );
    if (regexReturn?.groups) {
        return { ...formProps, ...regexReturn.groups };
    }
    // Fallback to simple regex matching
    const portalIdMatch = unescapedData.match(/portalId\:\s*\"(\d+)\"/);
    const formIdMatch = unescapedData.match(/formId\:\s*\"([\w-]+)\"/);

    if (portalIdMatch?.[1] && formIdMatch?.[1]) {
        return {
            ...formProps,
            portalId: portalIdMatch[1],
            formId: formIdMatch[1]
        };
    }

    return null;
};

const hubspotData = computed(() => {
    const unescapedData = unescape(props.embedScript);
    return parseHubSpotData(unescapedData);
})

const loadHubSpotScript = () => {
    return new Promise((resolve) => {
        if (typeof hbspt !== 'undefined') { resolve(); return; }
        if (document.getElementById('hs-forms-embed')) {
            // Script tag exists, wait for it to load
            const check = setInterval(() => {
                if (typeof hbspt !== 'undefined') { clearInterval(check); resolve(); }
            }, 200);
            return;
        }
        const script = document.createElement('script');
        script.id = 'hs-forms-embed';
        script.src = '//js.hsforms.net/forms/embed/v2.js';
        script.charset = 'utf-8';
        script.onload = () => {
            const check = setInterval(() => {
                if (typeof hbspt !== 'undefined') { clearInterval(check); resolve(); }
            }, 200);
        };
        document.head.appendChild(script);
    });
};

const initHubSpot = async () => {
    if (!hubspotData.value) {
        console.error('Could not parse portalId and formId from data');
        return;
    }
    await loadHubSpotScript();
    hbspt.forms.create(hubspotData.value);
    loading.value = false;
};

// The shard is decorative and only works when the column is tall enough to seat
// it BELOW the copy. Its own height is 440px, and the column's height comes from
// whichever side of the split is taller — so on a short embedded form (the
// homepage "Sold Out" instance) there is nowhere for it to go and it lands on
// top of the subhead. Rather than let it force the block taller to make room,
// measure the room actually available and hide it when there isn't any.
//
// Safe to measure: the shard is positioned absolutely, so it contributes no
// height and toggling it cannot feed back into the number being measured.
const SHARD_HEIGHT = 440;
const headerRef = ref(null);
const headerTextRef = ref(null);
const showShard = ref(false);
let observer = null;

const measureShardRoom = () => {
    const column = headerRef.value;
    const text = headerTextRef.value?.$el ?? headerTextRef.value;
    if (!column || !text) return;
    const room = column.getBoundingClientRect().height - text.getBoundingClientRect().height;
    showShard.value = room >= SHARD_HEIGHT;
};

onMounted(() => {
    setTimeout(() => { initHubSpot() }, 500);

    if (typeof ResizeObserver === 'undefined') return;
    // Watches both boxes: the column grows when the HubSpot form finishes
    // rendering (which is async and well after mount), and the text box grows
    // when the headline rewraps on resize.
    observer = new ResizeObserver(measureShardRoom);
    if (headerRef.value) observer.observe(headerRef.value);
    const text = headerTextRef.value?.$el ?? headerTextRef.value;
    if (text) observer.observe(text);
    measureShardRoom();
});

onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
});

</script>

<template>
    <section class="block-become-a-sponsor-form relative overflow-clip text-(--magenta) bg-pink pt-[60px] pb-[60px] lg:pt-28.25 lg:pb-[60px]" :class="{ 'blog-padding': isBlog, 'force-white': forceWhite }" tabindex="-1" id="sponsor-form">
        <div class="relative z-1 px-(--box-padding-lr-m) lg:px-(--box-padding-lr)">
            <div class="split flex flex-col gap-[30px] lg:flex-row lg:gap-[60px]">
                <div ref="headerRef" class="header lg:flex-[1_1_50%] flex flex-col lg:relative">
                    <div ref="headerTextRef" :class="['header-wrapper p-0', { 'lg:pb-22.5': !isBlog }]">
                        <MessHtml v-if="headline" tag="h2" class="font-hubspot-serif font-normal text-[56px] leading-[1.1] tracking-normal lg:text-[calc(86/var(--base-fs)*1rem)] relative z-1 mb-4" :html="stripBlockTags(headline)" />
                        <MessHtml v-if="subHead" class="copy font-hubspot-sans text-base leading-[26px] font-light tracking-[0.32px] relative z-1" :html="subHead" tag="div" />
                    </div>
                    <!-- Absolute rather than in flow: in flow the shard measured 560px and
                         forced the whole block taller whenever the embedded form was
                         shorter than headline + shard, which left 607px of empty scroll
                         below the homepage "Sold Out" copy. `showShard` then hides it
                         entirely on those short instances, where there is no room to seat
                         it below the copy and it would otherwise sit on top of the text. -->
                    <div v-if="showShard" class="hidden lg:display-block pointer-events-none z-0 lg:absolute lg:left-0 lg:bottom-[-60px]" aria-hidden="true">
                        <img src="/img/2026/shard-registration.png" loading="lazy" decoding="async" alt="" class="display-block w-[541px] max-w-none -ml-[40px]" />
                    </div>
                </div>
                <div class="form bg-white rounded-(--radius) p-(--box-padding-lr-m) lg:p-(--box-padding-lr) lg:flex-[1_1_50%] lg:self-start flex flex-col">
                    <ClientOnly>
                        <div class="form-wrapper relative">
                            <div :id="formID" class="form-container relative z-1" />
                            <FragmentsLoading v-if="loading" :loading="loading" class="my-4" />
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </div>
        <!-- Mobile shard -->
        <img src="/img/2026/register-mobile-bottom-shard.png" loading="lazy" decoding="async" alt="" aria-hidden="true"
            class="lg:hidden absolute bottom-0 left-0 w-full h-auto pointer-events-none z-[0]" />
    </section>
</template>

<style>

/* Blog model - 2026 styles */
[data-model='blog'] .builder-blocks .block-become-a-sponsor-form {
    background: white;
    color: var(--magenta);
    border-radius: var(--radius);
    padding: 1rem;
    width: 100%;
    overflow: hidden;
}

@media (min-width: 1024px) {
    [data-model='blog'] .builder-blocks .block-become-a-sponsor-form {
        padding: 1.75rem;
    }
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .header {
    padding: 0;
    width: 100%;
    margin-bottom: 0;
    color: var(--magenta);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .header .header-wrapper {
    background: none;
    padding: 0;
    margin: 0;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .header .header-wrapper h2 {
    font-family: var(--font-hubspot-serif);
    font-weight: 400;
    font-size: calc(24 / var(--base-fs) * 1rem);
    line-height: 1.3;
    color: var(--magenta);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .header .header-wrapper p {
    margin: 0;
    font-weight: 300;
    color: var(--magenta);
}

/* Hide shard in blog context */
[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .header > div[aria-hidden="true"] {
    display: none;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field label {
    color: var(--magenta);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-richtext {
    color: var(--magenta);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field.hs-email {
    width: 100%;
    display: block;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field.hs-email:has(+ .hs-phone) {
    width: calc(50% - (0.5 * var(--gap)));
    display: inline-block;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field input,
[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field select {
    border-color: #A55C87;
    color: var(--magenta);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field input::placeholder,
[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field select::placeholder {
    color: #A55C87;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field input.error,
[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-form-field select.error {
    border-color: var(--error);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-richtext a {
    color: var(--dark-pink);
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-button {
    margin-top: 1.5rem;
    background-color: var(--magenta);
    border: 1px solid var(--magenta);
    color: white;
    text-transform: capitalize;
}

@media (hover: hover) {
    [data-model='blog'] .builder-blocks .block-become-a-sponsor-form .hs-button:hover {
        background-color: var(--orange);
        border-color: var(--orange);
        color: white;
    }
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .container {
    min-width: 100%;
    width: 100%;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .form {
    padding: 0;
    background: transparent;
    width: 100%;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form > div {
    padding: 0;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .split {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

[data-model='blog'] .builder-blocks .block-become-a-sponsor-form .split > div {
    flex: 1 1 100%;
}

/* Main component styles - HubSpot form elements */
.block-become-a-sponsor-form .form-container form .hs-form-field label.hs-error-msg {
    color: var(--error);
}

.block-become-a-sponsor-form .hs-error-msgs {
    color: var(--error);
}

.block-become-a-sponsor-form .header h2 {
    margin-bottom: 1.4375rem;
}

.block-become-a-sponsor-form .subHead {
    position: relative;
    z-index: 1;
    margin-bottom: 2.5rem;
    letter-spacing: 0.51px;
}

.block-become-a-sponsor-form .subText {
    position: relative;
    z-index: 1;
}

.block-become-a-sponsor-form p {
    margin-bottom: 1rem;
}

.block-become-a-sponsor-form .form-container .subText {
    font-size: 0.6875rem;
    line-height: 1.1085625rem;
    color: var(--teal);
    margin-bottom: 2.9375rem;
}

.block-become-a-sponsor-form .hs-richtext {
    font-size: 0.6875rem;
    line-height: 1.1085625rem;
    letter-spacing: 0;
    color: var(--magenta);
}

.block-become-a-sponsor-form .hs-richtext a {
    color: var(--dark-pink);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: 2px;
    text-underline-position: from-font;
    transition: color 0.2s ease;
}

@media (hover: hover) {
    .block-become-a-sponsor-form .hs-richtext a:hover {
        color: var(--magenta);
    }
}

.block-become-a-sponsor-form .hs-richtext a strong {
    font-weight: 300;
}

.block-become-a-sponsor-form .form-container form {
    font-family: var(--font-hubspot-sans);
}

.block-become-a-sponsor-form .hs-form-field {
    --gap: 46px;
    position: relative;
    font-weight: 400;
    margin-bottom: 1.5625rem;
}

@media (min-width: 1024px) {
    .block-become-a-sponsor-form .hs-form-field {
        margin-bottom: 1.5625rem;
        width: 100%;
    }

    .block-become-a-sponsor-form .hs-form-field:not(.hs-company) {
        width: calc(50% - (0.5 * var(--gap)));
        display: inline-block;
        vertical-align: top;
    }

    .block-become-a-sponsor-form .hs-form-field:not(.hs-company):nth-child(even) {
        margin-left: var(--gap);
    }
}

.block-become-a-sponsor-form .hs-form-field label {
    display: block;
    margin-bottom: 0.5625rem;
    font-size: 0.8125rem;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 1.310125rem;
    text-transform: capitalize;
    color: var(--magenta);
}

.block-become-a-sponsor-form .hs-form-field label:focus {
    color: var(--teal);
}

.block-become-a-sponsor-form .hs-form-field input,
.block-become-a-sponsor-form .hs-form-field select {
    width: 100%;
    display: block;
    padding: 1.25rem 1.5rem;
    font-weight: 300;
    border-radius: 50px;
    border: 1px solid #A55C87;
    font-size: 1rem;
    letter-spacing: 0;
}

.block-become-a-sponsor-form .hs-form-field select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='8' viewBox='0 0 14 8' fill='none'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%2346062B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1.5rem center;
    padding-right: 3.5rem;
}

.block-become-a-sponsor-form .hs-form-field input::placeholder,
.block-become-a-sponsor-form .hs-form-field select::placeholder {
    color: #A55C87;
    letter-spacing: 0;
}

.block-become-a-sponsor-form .hs-form-field input:focus,
.block-become-a-sponsor-form .hs-form-field select:focus {
    border: 1px solid var(--cream);
}

.block-become-a-sponsor-form .hs-form-field .hs-form-required {
    color: var(--orange-dark);
}

.block-become-a-sponsor-form .hs-form-field .error {
    border-color: var(--orange-dark);
    color: var(--orange-dark);
}

.block-become-a-sponsor-form .hs-form-field .hs-error-msgs {
    position: static;
    color: var(--orange-dark);
    margin-top: 0.5625rem;
}

.block-become-a-sponsor-form .hs-form-field .hs-error-msgs label {
    color: var(--orange-dark);
    font-weight: 400;
}

.block-become-a-sponsor-form .hs-form-field .hs-error-msgs label a {
    color: var(--orange-dark);
    font-weight: 400;
}

.block-become-a-sponsor-form .hs_error_rollup {
    font-weight: 500;
    color: var(--orange-dark);
    margin-top: 1.46875rem;
}

.block-become-a-sponsor-form .submitted-message {
    font-family: var(--font-hubspot-sans);
    color: var(--magenta);
}

.block-become-a-sponsor-form .submitted-message a {
    color: var(--magenta);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-skip-ink: none;
    text-underline-offset: 2px;
    transition: color 0.2s ease, text-decoration 0.2s ease;
}

@media (hover: hover) {
    .block-become-a-sponsor-form .submitted-message a:hover {
        color: var(--magenta);
        text-decoration: none;
    }
}

/* Button styles — 2026 pill */
.block-become-a-sponsor-form .hs-button {
    margin-top: 1.25rem;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    background-color: var(--magenta);
    border-radius: 50px;
    border: none;
    font-family: var(--font-hubspot-serif);
    font-size: 17px;
    font-weight: 400;
    line-height: 1.4;
    letter-spacing: 0.34px;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    text-transform: none;
}

@media (hover: hover) {
    .block-become-a-sponsor-form .hs-button:hover {
        background-color: var(--orange);
        color: white;
    }
}

.block-become-a-sponsor-form .hs-button span {
    position: relative;
    z-index: 1;
}

/* Blog padding layout */
.block-become-a-sponsor-form.blog-padding {
    padding: 3rem 0;
}

.block-become-a-sponsor-form.blog-padding .narrow-container {
    padding: 0 var(--inner-padding);
}

/* Responsive styles */
@media (max-width: 768px) {
    .block-become-a-sponsor-form .decoration-svg {
        width: 78px;
        height: 78px;
    }

    .block-become-a-sponsor-form .narrow-container .h1.subHead {
        margin-bottom: 2rem;
    }

    .block-become-a-sponsor-form .narrow-container .subHead {
        letter-spacing: 0.48px;
    }

    .block-become-a-sponsor-form .narrow-container .hs-form-field:not(.hs-company) {
        display: block;
        width: 100%;
    }

    .block-become-a-sponsor-form .narrow-container .hs-form-field:nth-child(even) {
        margin-left: 0;
    }

    .block-become-a-sponsor-form .narrow-container .hs-form-field input,
    .block-become-a-sponsor-form .narrow-container .hs-form-field select {
        height: 48px;
    }
}

@media (max-width: 500px) {
    .block-become-a-sponsor-form .hs-error-msgs {
        bottom: -1.8rem;
        font-size: 0.92rem;
    }
}

@media (max-width: 400px) {
    .block-become-a-sponsor-form .hs-error-msgs {
        font-size: 0.75rem;
    }
}

/* Multi-checkbox fields (e.g. the new sponsor-interest fields) span the full
   form width instead of being squeezed into the 50% desktop column, and their
   checkboxes flow into 2 columns (3 at tablet+). Scoped via :has() to a list
   with 2+ checkboxes so single consent/boolean checkboxes are unaffected. */
.block-become-a-sponsor-form .hs-form-field:has(ul.inputs-list li.hs-form-checkbox + li.hs-form-checkbox) {
    width: 100%;
    display: block;
    margin-left: 0;
}

.block-become-a-sponsor-form .hs-form-field ul.inputs-list:has(li.hs-form-checkbox + li.hs-form-checkbox) {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 24px;
}

@media (min-width: 768px) {
    .block-become-a-sponsor-form .hs-form-field ul.inputs-list:has(li.hs-form-checkbox + li.hs-form-checkbox) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

/* Select fields (e.g. Sponsor Budget) sit on their own row → full width, no
   indent (the 50%-column + even-child margin rule above otherwise leaves them
   half-width and pushed right). :not(.hs-company) matches the even-child rule's
   specificity so this wins on source order. */
.block-become-a-sponsor-form .hs-form-field.hs-fieldtype-select:not(.hs-company) {
    width: 100%;
    display: block;
    margin-left: 0;
}

/* Checkbox + label inline, vertically centered. The generic text-input rule
   above (width:100%; display:block) also lands on checkbox inputs and stacks
   them above their labels — reset that and lay each option out as a centered
   checkbox-then-label flex row. High specificity to beat HubSpot's own CSS. */
.block-become-a-sponsor-form .hs-fieldtype-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    display: inline-block;
    padding: 0;
    border-radius: 3px;
    margin: 0;
    flex-shrink: 0;
}

.block-become-a-sponsor-form .hs-fieldtype-checkbox li.hs-form-checkbox label.hs-form-checkbox-display {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 300;
    text-transform: none;
    cursor: pointer;
}

/* Differentiate the field question ("What's your ICP?") from the option labels —
   the question reads heavier; the options stay light. */
.block-become-a-sponsor-form .hs-fieldtype-checkbox > label {
    font-weight: 500;
    font-size: calc(15 / var(--base-fs) * 1rem);
    color: var(--magenta);
    text-transform: none;
    margin-bottom: 0.75rem;
}
</style>
